/**
 * TUVI - Información Básica del Proyecto
 * Capítulo con todos los datos básicos y de configuración inicial
 * Con funcionalidad de edición
 */

import React, { useState, useEffect } from 'react';
import client from '../../../services/graphqlClient';
import { useNotification } from '../../../context/NotificationContext';
import { UPLOAD_URL } from '../../../config';
import './InformacionBasica.css';

const COMERCIALES = ['Natalia', 'Ana Sofia', 'Veronica'];

const InformacionBasica = ({ proyecto, onProyectoActualizado }) => {
  const { success: notifySuccess, error: notifyError } = useNotification();
  const [modoEdicion, setModoEdicion] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [formData, setFormData] = useState({});
  const [residentes, setResidentes] = useState([]);
  const [enviandoCorreo, setEnviandoCorreo] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  useEffect(() => {
    if (proyecto?.proyecto) {
      setFormData({ ...proyecto.proyecto });
    }
  }, [proyecto]);

  useEffect(() => {
    // Cargar residentes desde la API de administrativo
    const cargarResidentes = async () => {
      try {
        const data = await client.query(`{ residentes { id nombre correo } }`);
        setResidentes(data.residentes || []);
      } catch (err) {}
    };
    cargarResidentes();
  }, []);

  if (!proyecto) return null;

  const { proyecto: info, consideraciones, plan_pagos } = proyecto;

  // Formatear moneda
  const formatMoney = (valor) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor || 0);
  };

  // Formatear fecha
  const formatDate = (fecha) => {
    if (!fecha) return 'No definida';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Formatear fecha para input
  const formatDateInput = (fecha) => {
    if (!fecha) return '';
    const date = new Date(fecha);
    return date.toISOString().split('T')[0];
  };

  const handleInputChange = (campo, valor) => {
    setFormData(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const handleCancelar = () => {
    setFormData({ ...proyecto.proyecto });
    setModoEdicion(false);
    setMensaje(null);
  };

  const enviarCorreoResidente = async () => {
    const residenteNombre = info.residente;
    if (!residenteNombre) { notifyError('No hay residente asignado'); return; }
    const residenteData = residentes.find(r => r.nombre === residenteNombre);
    if (!residenteData?.correo) { notifyError('El residente no tiene correo registrado'); return; }
    if (!window.confirm(`Enviar documentos del proyecto a ${residenteNombre} (${residenteData.correo})?\n\nSe generarán y enviarán:\n- Cronograma interno\n- Ficha FFE\n- Contratistas`)) return;

    setEnviandoCorreo(true);
    try {
      const { generarYSubirPDFs } = await import('../../../services/pdfGeneratorService');
      const proyId = info.id;
      const proyNombre = info.proyecto_nombre || 'Proyecto';
      const apto = info.apartamento || '';

      // 1. Generar PDFs y subir a MinIO (temp-documentos) uno por uno
      const keys = await generarYSubirPDFs(proyId, proyNombre, apto);

      if (keys.length === 0) { notifyError('No se pudo generar ningún PDF'); return; }

      // 2. Enviar correo pasando solo las keys
      const token = localStorage.getItem('tuvi_token');
      const res = await fetch(`${UPLOAD_URL}/enviar-residente`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: residenteData.correo,
          proyecto: proyNombre,
          apartamento: apto,
          residente: residenteNombre,
          keys,
        }),
      });
      const result = await res.json();
      if (result.success) notifySuccess(result.message);
      else notifyError(result.message);
    } catch (err) {
      console.error('Error enviando correo:', err);
      notifyError('Error al enviar correo');
    } finally {
      setEnviandoCorreo(false);
    }
  };

  // Sanitiza fechas: solo acepta formato YYYY-MM-DD, lo demás se envía como null
  const sanitizeFecha = (val) => {
    if (!val || typeof val !== 'string') return null;
    const limpio = val.split('T')[0];
    return /^\d{4}-\d{2}-\d{2}$/.test(limpio) ? limpio : null;
  };

  const handleGuardar = async () => {
    try {
      setGuardando(true);
      setMensaje(null);

      const data = await client.mutate(`
        mutation($id: Int!, $input: ActualizarProyectoInput!) {
          actualizarProyecto(id: $id, input: $input) { success message }
        }
      `, {
        id: formData.id,
        input: {
          proyecto_nombre: formData.proyecto_nombre,
          apartamento: formData.apartamento,
          area_m2: formData.area_m2 ? parseFloat(formData.area_m2) : null,
          habitaciones: formData.habitaciones ? parseInt(formData.habitaciones) : null,
          banos: formData.banos ? parseInt(formData.banos) : null,
          nombre_cliente: formData.nombre_cliente,
          cedula: formData.cedula,
          telefono: formData.telefono,
          correo: formData.correo,
          negocio: formData.negocio,
          ubicacion_detallada: formData.ubicacion_detallada,
          tipo_cocina: formData.tipo_cocina,
          direccion: formData.direccion,
          comercial: formData.comercial,
          residente: formData.residente,
          contratista: formData.contratista,
          fecha_cotizacion: sanitizeFecha(formData.fecha_cotizacion),
          fecha_inicio_cronograma: sanitizeFecha(formData.fecha_inicio_cronograma),
          fecha_entrega_programada: sanitizeFecha(formData.fecha_entrega_programada),
        }
      });

      if (data.actualizarProyecto.success) {
        setMensaje({ tipo: 'exito', texto: 'Información actualizada correctamente' });
        setModoEdicion(false);
        if (onProyectoActualizado) {
          onProyectoActualizado();
        }
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error('Error al guardar:', error);
      setMensaje({ tipo: 'error', texto: error.message || 'Error al guardar los cambios' });
    } finally {
      setGuardando(false);
    }
  };

  // Funciones helper para renderizar campos (NO son componentes React)
  const renderCampoEditable = (label, campo, tipo = 'text', placeholder = '') => {
    const valor = modoEdicion ? (formData[campo] ?? '') : (info[campo] || 'No especificado');

    if (modoEdicion) {
      return (
        <div className="data-item" key={campo}>
          <label>{label}</label>
          <input
            type={tipo}
            className="data-input"
            value={valor}
            onChange={(e) => handleInputChange(campo, e.target.value)}
            placeholder={placeholder}
          />
        </div>
      );
    }

    return (
      <div className="data-item" key={campo}>
        <label>{label}</label>
        <div className="data-value">{valor}</div>
      </div>
    );
  };

  const renderCampoFechaEditable = (label, campo, icono) => {
    const valorDisplay = formatDate(info[campo]);
    const valorInput = formatDateInput(formData[campo]);

    if (modoEdicion) {
      return (
        <div className="fecha-item editable" key={campo}>
          <span className="fecha-icon">{icono}</span>
          <div className="fecha-content">
            <span className="fecha-label">{label}</span>
            <input
              type="date"
              className="fecha-input"
              value={valorInput}
              onChange={(e) => handleInputChange(campo, e.target.value)}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="fecha-item" key={campo}>
        <span className="fecha-icon">{icono}</span>
        <div className="fecha-content">
          <span className="fecha-label">{label}</span>
          <span className="fecha-value">{valorDisplay}</span>
        </div>
      </div>
    );
  };

  const renderMiembroEquipoEditable = (icono, rol, campo) => {
    const valor = modoEdicion ? (formData[campo] ?? '') : (info[campo] || 'No asignado');

    if (modoEdicion) {
      return (
        <div className="team-member editable" key={campo}>
          <div className="member-icon">{icono}</div>
          <div className="member-info">
            <span className="member-role">{rol}</span>
            <input
              type="text"
              className="member-input"
              value={valor}
              onChange={(e) => handleInputChange(campo, e.target.value)}
              placeholder={`Nombre del ${rol.toLowerCase()}`}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="team-member" key={campo}>
        <div className="member-icon">{icono}</div>
        <div className="member-info">
          <span className="member-role">{rol}</span>
          <span className="member-name">{valor}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="informacion-basica">
      <div className="capitulo-header">
        <div className="header-content">
          <div className="header-text">
            <h2>Informacion Basica del Proyecto</h2>
            <p>Datos generales, consideraciones y estructura del proyecto</p>
          </div>
          <div className="header-actions">
            {!modoEdicion ? (
              <button className="btn-editar" onClick={() => setModoEdicion(true)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
                Editar Informacion
              </button>
            ) : (
              <div className="edit-actions">
                <button className="btn-cancelar" onClick={handleCancelar} disabled={guardando}>
                  Cancelar
                </button>
                <button className="btn-guardar" onClick={handleGuardar} disabled={guardando}>
                  {guardando ? (
                    <>
                      <span className="spinner"></span>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                        <polyline points="17 21 17 13 7 13 7 21"></polyline>
                        <polyline points="7 3 7 8 15 8"></polyline>
                      </svg>
                      Guardar Cambios
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
        {mensaje && (
          <div className={`mensaje-feedback ${mensaje.tipo}`}>
            {mensaje.tipo === 'exito' ? '✓' : '✗'} {mensaje.texto}
          </div>
        )}
      </div>

      {/* Datos del Proyecto */}
      <div className="section-card">
        <h3>Datos del Proyecto</h3>
        <div className="data-grid">
          {renderCampoEditable("Nombre del Proyecto:", "proyecto_nombre")}
          {renderCampoEditable("Apartamento/Unidad:", "apartamento", "text", "Ej: Apto 301")}
          {renderCampoEditable("Negocio/Proyecto:", "negocio", "text", "Ej: Torres del Bosque")}
          {renderCampoEditable("Ubicacion Detallada:", "ubicacion_detallada", "text", "Ej: Calle 100 #15-20")}
          <div className="data-item">
            <label>Area Total:</label>
            {modoEdicion ? (
              <div className="input-with-suffix">
                <input
                  type="number"
                  className="data-input"
                  value={formData.area_m2 ?? ''}
                  onChange={(e) => handleInputChange('area_m2', e.target.value)}
                  placeholder="0"
                  step="0.01"
                />
                <span className="suffix">m2</span>
              </div>
            ) : (
              <div className="data-value highlight">{info.area_m2} m2</div>
            )}
          </div>
          {renderCampoEditable("Habitaciones:", "habitaciones", "number", "0")}
          {renderCampoEditable("Banos:", "banos", "number", "0")}
          {renderCampoEditable("Tipo de Cocina:", "tipo_cocina", "text", "Ej: Integral, Semi-integral")}
        </div>
      </div>

      {/* Información del Cliente */}
      <div className="section-card">
        <h3>Informacion del Cliente</h3>
        <div className="data-grid">
          {renderCampoEditable("Nombre Completo:", "nombre_cliente")}
          {renderCampoEditable("Cedula:", "cedula", "text", "Ej: 1234567890")}
          {renderCampoEditable("Direccion:", "direccion", "text", "Direccion del cliente")}
          {renderCampoEditable("Telefono:", "telefono", "text", "Ej: 3001234567")}
          {renderCampoEditable("Email:", "correo", "email", "correo@ejemplo.com")}
        </div>
      </div>

      {/* Equipo de Trabajo */}
      <div className="section-card">
        <h3>Equipo de Trabajo</h3>
        <div className="team-grid">
          {/* Comercial - editable */}
          <div className={`team-member${modoEdicion ? ' editable' : ''}`}>
            <div className="member-icon">💼</div>
            <div className="member-info">
              <span className="member-role">Comercial</span>
              {modoEdicion ? (
                <input
                  type="text"
                  className="member-input"
                  value={formData.comercial || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, comercial: e.target.value }))}
                  placeholder="Nombre del comercial"
                />
              ) : (
                <span className="member-name">{info.comercial || 'No asignado'}</span>
              )}
            </div>
          </div>
          {/* Residente - dropdown desde API */}
          <div className={`team-member${modoEdicion ? ' editable' : ''}`}>
            <div className="member-icon">👷</div>
            <div className="member-info">
              <span className="member-role">Residente de Obra</span>
              {modoEdicion ? (
                <select
                  className="member-input"
                  value={formData.residente || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, residente: e.target.value }))}
                >
                  <option value="">Seleccionar...</option>
                  {residentes.map(r => (
                    <option key={r.id} value={r.nombre}>{r.nombre}</option>
                  ))}
                </select>
              ) : (
                <span className="member-name">{info.residente || 'No asignado'}</span>
              )}
              {info.residente && info.estado === 'en_ejecucion' && (
                <button
                  className="btn-enviar-residente"
                  onClick={enviarCorreoResidente}
                  disabled={enviandoCorreo}
                  title="Enviar documentos al residente por correo"
                >
                  {enviandoCorreo ? 'Enviando...' : '📧 Enviar Documentos'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Información Financiera Detallada - Solo lectura */}
      <div className="section-card">
        <h3>Informacion Financiera</h3>
        <div className="financial-breakdown">
          <div className="financial-row">
            <span className="financial-label">Subtotal:</span>
            <span className="financial-amount">{formatMoney(info.subtotal)}</span>
          </div>
          <div className="financial-row">
            <span className="financial-label">AIU (15%):</span>
            <span className="financial-amount">
              {formatMoney(info.aiu)}
            </span>
          </div>
          <div className="financial-row">
            <span className="financial-label">IVA (19%):</span>
            <span className="financial-amount">
              {formatMoney(info.iva)}
            </span>
          </div>
          <div className="financial-row total">
            <span className="financial-label">Valor Final del Proyecto:</span>
            <span className="financial-amount">{formatMoney(info.valor_final)}</span>
          </div>
        </div>
      </div>

      {/* Consideraciones - Solo lectura */}
      {consideraciones && consideraciones.length > 0 && (
        <div className="section-card">
          <h3>Consideraciones del Proyecto</h3>
          <div className="consideraciones-list">
            {consideraciones.map((consideracion, index) => (
              <div key={consideracion.id} className="consideracion-item">
                <span className="consideracion-number">{index + 1}</span>
                <span className="consideracion-text">{consideracion.descripcion}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Plan de Pagos - Solo lectura */}
      {plan_pagos && plan_pagos.length > 0 && (
        <div className="section-card">
          <h3>Plan de Pagos</h3>
          <div className="plan-pagos-table">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Descripcion</th>
                  <th>Porcentaje</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                {plan_pagos.map((pago, index) => (
                  <tr key={pago.id}>
                    <td>{index + 1}</td>
                    <td>{pago.descripcion}</td>
                    <td>
                      <span className="porcentaje-badge">{pago.porcentaje}%</span>
                    </td>
                    <td className="valor-cell">{formatMoney(pago.valor)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="2"><strong>Total</strong></td>
                  <td>
                    <strong>
                      {plan_pagos.reduce((sum, p) => sum + p.porcentaje, 0)}%
                    </strong>
                  </td>
                  <td className="valor-cell">
                    <strong>
                      {formatMoney(plan_pagos.reduce((sum, p) => sum + p.valor, 0))}
                    </strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Fechas Importantes - eliminado, ahora está sincronizado en Resumen desde el cronograma */}

      {/* Estado General - Solo lectura */}
      <div className="section-card">
        <h3>Estado General del Proyecto</h3>
        <div className="estado-grid">
          <div className="estado-item">
            <label>Estado Actual:</label>
            <span className={`estado-badge ${info.estado}`}>
              {info.estado.replace('_', ' ')}
            </span>
          </div>
          <div className="estado-item">
            <label>Progreso General:</label>
            <div className="progress-container">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${info.progreso_general || 0}%` }}
                ></div>
              </div>
              <span className="progress-text">{info.progreso_general || 0}%</span>
            </div>
          </div>
          {info.dias_retraso > 0 && (
            <div className="estado-item alert">
              <label>Dias de Retraso:</label>
              <div className="data-value">{info.dias_retraso} dias</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InformacionBasica;
